import { getStaticMetricsBundles } from '@/views/reports/report-metrics-bundles-static';
import { resolveRouteMetricsBundle } from '@/views/reports/report-metrics-bundles-merge';
import {
  buildAdditionalMainColumns,
  buildMainMetricsColumns,
  buildMetricColumn,
  resolveChartMetricYAxis,
  resolveChartMetricYAxisSuffix,
  resolveMetricsList,
  resolveReportMetricName,
  type DedicatedMetricsBundles,
} from '@/views/reports/report-metrics-runtime';
import { isDedicatedReportRoute } from '@/views/reports/report-route-behavior';

export type ReportMetricsStoreBindings = {
  getCustomEvents: () => Record<string, unknown>;
  getCustomColumns: () => Record<
    string,
    { formula?: string; description?: string; format?: string }
  >;
  getDimensions: () => Record<
    string,
    { mainColumn?: string; additionalColumns?: string[] }
  >;
  getMetrics: () => string[];
  getIsHideFilters: () => boolean;
  getIndependentColumnSettingsEnabled: () => boolean;
};

export type CreateReportMetricsApiOptions = {
  translate: (name: string) => string;
  store: ReportMetricsStoreBindings;
  getTableQueryForCurrentTab: () => Record<string, unknown>;
  dedicatedMetricsBundles?: DedicatedMetricsBundles;
  getDedicatedMetricsBundles?: () => DedicatedMetricsBundles;
};

export const createReportMetricsApi = (options: CreateReportMetricsApiOptions) => {
  const { translate, store } = options;

  const resolveDedicatedMetricsBundles = () => {
    if (options.getDedicatedMetricsBundles) {
      return options.getDedicatedMetricsBundles();
    }

    return options.dedicatedMetricsBundles ?? getStaticMetricsBundles();
  };

  const reportMetricsName = (key: string) => {
    return resolveReportMetricName(key, {
      translate,
      customEvents: store.getCustomEvents(),
      getCustomColumns: () => store.getCustomColumns(),
    });
  };

  const getCustomColumnFormat = (pureKey: string) => {
    return store.getCustomColumns()[pureKey]?.format;
  };

  const getChartMetricYAxisSuffix = (
    key: string,
    seriesYAxis?: number,
    currencySuffix = '',
  ) => {
    return resolveChartMetricYAxisSuffix({
      key,
      currencySuffix,
      getCustomColumnFormat,
      seriesYAxis,
    });
  };

  const getChartMetricYAxis = (key: string, seriesYAxis?: number) => {
    return resolveChartMetricYAxis({
      key,
      getCustomColumnFormat,
      seriesYAxis,
    });
  };

  const getCustomMetrics = (routeName: string) => {
    return resolveRouteMetricsBundle(routeName, resolveDedicatedMetricsBundles());
  };

  const createColumn = (value: string, columnOptions?: Record<string, unknown>) => {
    return buildMetricColumn(value, reportMetricsName, columnOptions);
  };

  const existingCustomMetrics = (routeName: string) => {
    const customMetrics = getCustomMetrics(routeName) as { avaliable_metrics: string[] };
    return customMetrics.avaliable_metrics.map(value => createColumn(value));
  };

  const additionalMainColumns = () => {
    return buildAdditionalMainColumns({
      treeLevels: options.getTableQueryForCurrentTab().treeLevels as string[] | undefined,
      dimensions: store.getDimensions() as Record<string, { mainColumn: string }>,
      reportMetricsName,
    });
  };

  const mainMetrics = (type: string, routeName: string) => {
    const customMetricsBundle = getCustomMetrics(routeName) as {
      report_exclude: Record<string, string[]>;
    };

    return buildMainMetricsColumns({
      type,
      routeName,
      dimensions: store.getDimensions(),
      metrics: store.getMetrics(),
      isHideFilters: store.getIsHideFilters(),
      additionalMainColumns: additionalMainColumns(),
      customMetricsBundle,
      reportMetricsName,
    });
  };

  const getMetricsList = (routeName: string, type: string) => {
    return resolveMetricsList({
      routeName,
      type,
      dedicatedRoute: isDedicatedReportRoute(routeName),
      existingCustomMetrics: existingCustomMetrics(routeName),
      mainMetrics: mainMetrics(type, routeName),
    });
  };

  return {
    reportMetricsName,
    getChartMetricYAxisSuffix,
    getChartMetricYAxis,
    createColumn,
    getMetricsList,
    mainMetrics,
    additionalMainColumns,
    existingCustomMetrics,
    getCustomMetrics,
  };
};
